import type { RecoveryStateView, VerificationStateView } from '#src/generated/protocol';

export type SetupStep =
  | 'device'
  | 'recovery'
  | 'profile'
  | 'notifications'
  | 'appearance'
  | 'layout'
  | 'sync'
  | 'consent'
  | 'done';
export type AccountStep = Extract<SetupStep, 'recovery' | 'profile' | 'notifications'>;

export const ACCOUNT_STEPS: readonly AccountStep[] = ['recovery', 'profile', 'notifications'];

export interface SetupState {
  registering: boolean;
  verification: VerificationStateView;
  recovery: RecoveryStateView;
  newRecoveryKey: boolean;
  accountFinished: readonly AccountStep[];
  permissionAskable: boolean;
  syncEnabled: boolean;
  consentPending: boolean;
}

export function isAccountStep(step: SetupStep): step is AccountStep {
  return (ACCOUNT_STEPS as readonly SetupStep[]).includes(step);
}

export function encryptionKnown(state: Pick<SetupState, 'verification' | 'recovery'>): boolean {
  return state.verification !== 'unknown' && state.recovery !== 'unknown';
}

// wa-sable: confirming this session and creating recovery never gate the entry. An unverified
// session or an account without recovery starts no setup; the sidebar banners stay to do it later.
export function setupNeeded(state: Pick<SetupState, 'registering'>): boolean {
  return state.registering;
}

// wa-sable: recovery is planned only to show a key that was just generated, never to create one.
function recoveryWanted(state: SetupState): boolean {
  return state.newRecoveryKey;
}

export function planSetup(state: SetupState, full: boolean): SetupStep[] {
  const steps: SetupStep[] = [];
  if (full) {
    if (recoveryWanted(state)) steps.push('recovery');
    if (state.registering && !state.accountFinished.includes('profile')) steps.push('profile');
    if (!state.accountFinished.includes('notifications') || state.permissionAskable) {
      steps.push('notifications');
    }
    if (state.registering) steps.push('appearance', 'layout');
    if (!state.syncEnabled) steps.push('sync');
  }
  if (state.consentPending) steps.push('consent');
  if (full) steps.push('done');
  return steps;
}

export function replan(
  finished: readonly SetupStep[],
  state: SetupState,
  full: boolean
): SetupStep[] {
  return [...finished, ...planSetup(state, full).filter((step) => !finished.includes(step))];
}
